import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, MapPin, ArrowRight, User, Phone, Send, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bot-safe email display.
// The plain address is NEVER present in the shipped HTML or JS bundle: only
// two independent base64 fragments are stored, decoded at runtime inside the
// browser, then joined via a separator ("@") that lives in a third variable.
// Rendered as plain text — no mailto link — so scrapers that follow anchors
// see nothing usable.
const ObfuscatedEmail = ({ className = '' }) => {
  const [email, setEmail] = useState('');
  useEffect(() => {
    try {
      const u = atob('a29udGFrdA==');            // "kontakt"
      const d = atob('c21hcnQtcG9zdC1zb2x1dGlvbi5kZQ=='); // "smart-post-solution.de"
      const sep = String.fromCharCode(64);       // "@"
      setEmail(u + sep + d);
    } catch {
      /* silently fall back to nothing rather than leaking */
    }
  }, []);
  // Rendered as plain <span> — deliberately not a link.
  return (
    <span className={className} data-testid="obfuscated-email">
      {email}
    </span>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  // Honeypot — never rendered visibly; bots that fill *all* fields blindly are dropped server-side.
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, website }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Serverfehler (${res.status})`);
      }
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setWebsite('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Bitte versuchen Sie es später erneut.');
    }
  };

  return (
    <section id="kontakt" className="py-20 lg:py-32 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-slate-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-20"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Lassen Sie uns sprechen
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Unverbindliche Kurzbewertung oder ausführliches Erstgespräch — wählen Sie, was für Sie passt.
            </p>
          </div>

          {/* Contact form */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 p-8 lg:p-10 mb-8 shadow-lg" data-testid="contact-form-card">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Option 1: Kurzbewertung</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  20–30 Minuten zur schnellen Einordnung Ihrer Versandstruktur und ersten Potenzialabschätzung.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Option 2: Erstgespräch</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Ausführlicher Austausch über Kosten, Risiken und konkrete Umsetzbarkeit für Ihre Situation.
                </p>
              </div>
            </div>

            {status === 'success' ? (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3"
                data-testid="contact-form-success"
              >
                <CheckCircle2 className="text-emerald-600 mt-0.5 flex-shrink-0" size={22} />
                <div>
                  <div className="font-semibold text-emerald-900 mb-1">Vielen Dank für Ihre Nachricht.</div>
                  <p className="text-sm text-emerald-800 leading-relaxed">
                    Ihre Anfrage ist bei uns angekommen. Sie erhalten in aller Regel innerhalb eines Werktages
                    eine persönliche Rückmeldung von Bernd Deninger.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-3 text-sm font-medium text-emerald-700 hover:text-emerald-900 underline"
                  >
                    Weitere Nachricht senden
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Name<span className="text-orange-500">*</span>
                    </label>
                    <Input
                      type="text"
                      required
                      minLength={2}
                      maxLength={120}
                      value={form.name}
                      onChange={update('name')}
                      placeholder="Vor- und Nachname"
                      className="bg-white"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      E-Mail<span className="text-orange-500">*</span>
                    </label>
                    <Input
                      type="email"
                      required
                      maxLength={200}
                      value={form.email}
                      onChange={update('email')}
                      placeholder="ihre.adresse@firma.de"
                      className="bg-white"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Betreff<span className="text-orange-500">*</span>
                  </label>
                  <Input
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                    value={form.subject}
                    onChange={update('subject')}
                    placeholder="Kurze Zusammenfassung Ihres Anliegens"
                    className="bg-white"
                    data-testid="contact-input-subject"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Nachricht<span className="text-orange-500">*</span>
                  </label>
                  <Textarea
                    required
                    minLength={10}
                    maxLength={5000}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Was liegt an? Sendungsvolumen, aktueller Anbieter, gewünschter Zeitrahmen …"
                    rows={6}
                    className="bg-white"
                    data-testid="contact-input-message"
                  />
                </div>

                {/* Honeypot — hidden from users and screen readers; bots that fill everything still get caught server-side. */}
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>
                </div>

                {status === 'error' && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="contact-form-error">
                    {errorMsg}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                  <Button
                    type="submit"
                    disabled={status === 'sending'}
                    className="text-slate-900 px-8 py-6 text-base h-auto group hover:shadow-xl transition-all duration-300 hover:scale-105 font-semibold disabled:opacity-70 disabled:hover:scale-100"
                    style={{ backgroundColor: '#FFA266' }}
                    data-testid="contact-submit-btn"
                  >
                    {status === 'sending' ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Wird gesendet …
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        Nachricht senden
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 max-w-sm leading-relaxed">
                    Alle Angaben werden vertraulich behandelt. Sie erhalten eine persönliche Rückmeldung.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* Non-email contact data — email address deliberately omitted here to prevent bot harvesting. */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 p-8 shadow-lg">
            <h3 className="text-xl font-semibold text-slate-900 mb-6">Kontaktdaten</h3>
            <div className="space-y-4">
              <div className="flex items-start group">
                <User className="text-slate-600 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Name</p>
                  <p className="text-slate-900 font-medium">Bernd Deninger</p>
                </div>
              </div>
              <div className="flex items-start group">
                <Mail className="text-slate-600 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm text-slate-500 mb-1">E-Mail</p>
                  <ObfuscatedEmail className="text-slate-900 font-medium" />
                  <p className="text-xs text-slate-500 mt-1">Am schnellsten erreichen Sie uns über das Kontaktformular oben.</p>
                </div>
              </div>
              <div className="flex items-start group">
                <Phone className="text-slate-600 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Office</p>
                  <a href="tel:+497235975942" className="text-slate-900 hover:text-slate-600 transition-colors font-medium">
                    07235 - 9759-427
                  </a>
                </div>
              </div>
              <div className="flex items-start group">
                <Phone className="text-slate-600 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Mobil</p>
                  <a href="tel:+4915233886484" className="text-slate-900 hover:text-slate-600 transition-colors font-medium">
                    0152 33 88 64 84
                  </a>
                </div>
              </div>
              <div className="flex items-start group">
                <MapPin className="text-slate-600 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-sm text-slate-500 mb-1">Büro</p>
                  <p className="text-slate-900">
                    Unterreichenbacher Straße 3<br />
                    75331 Engelsbrand
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
